import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ProductImage } from './ProductImage';
import { ProductBadge } from './ProductBadge';
import { ProductTitle } from './ProductTitle';
import { ProductPrice } from './ProductPrice';
import { ProductRating } from './ProductRating';
import { QuickAddButton } from './QuickAddButton';
import { PixelDecoration } from '@/components/shared/atoms/PixelDecoration';
import { Product } from '@/types/product';
import { motion } from 'framer-motion';

interface ProductCardProps {
  product: Product;
  className?: string;
}

export const ProductCard = ({ product, className = '' }: ProductCardProps) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const handleCardClick = () => {
    navigate(`/product/${product.slug}`);
  };

  const isOutOfStock = product.stockStatus === 'out_of_stock';

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={className}
    >
      <Card
        className="group cursor-pointer overflow-hidden bg-white border-2 border-gray-200 hover:border-brand-primary transition-all duration-300 hover:shadow-2xl hover:shadow-brand-primary/20 relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
      >
        {/* Pixel Decorations */}
        <PixelDecoration variant="corner" position="top-left" color="primary" />
        <PixelDecoration variant="corner" position="bottom-right" color="accent" />
        {isHovered && (
          <>
            <PixelDecoration variant="dot" position="top-right" color="neon-cyan" />
            <PixelDecoration variant="dot" position="bottom-left" color="neon-pink" />
          </>
        )}

        <div className="relative">
          <ProductBadge
            stockStatus={product.stockStatus}
            isNew={product.isNew}
            isSale={false}
          />

          <ProductImage
            images={product.images}
            alt={product.name}
          />

          {/* Quick Add Button - Shows on hover */}
          {isHovered && !isOutOfStock && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bottom-3 left-3 right-3"
            >
              <QuickAddButton
                onClick={() => navigate(`/product/${product.slug}`)}
                disabled={isOutOfStock}
              />
            </motion.div>
          )}
        </div>

        <CardContent className="p-4 space-y-3">
          <ProductTitle name={product.name} />

          {product.rating && (
            <ProductRating
              average={product.rating.average}
              count={product.rating.count}
            />
          )}

          <div className="flex items-center justify-between pt-2">
            <ProductPrice prices={product.prices} />

            {product.stockStatus === 'preorder' && (
              <span className="text-xs text-brand-accent font-semibold">
                {product.preorderShippingWeeks} weeks
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
